import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService, HttpResponse, HttpStatusEnum } from '@core/api';
import { displayOrderNotFoundPage } from '@shared/helpers';
import { EstimatedAmount, OrderDetailsReaction } from '@shared/interfaces';
import { OrderSampleDetails, OrderShort, SubmitRetestResponse } from '../interfaces';
import { SubmitRetestPayload } from '../interfaces/ckm-submit-retest-payload.interface';
import { PrepareStep3Interface } from '../interfaces/prepare-step3-data.interface';
import { OrderRetestSetupMapper } from './order-retest-setup.mapper';

@Injectable()
export class OrderRetestSetupApi {
    private readonly resourceUrl = 'retest-setup';

    constructor(
        private apiService: ApiService,
        private router: Router
    ) {}

    searchOrders(searchValue: string): Promise<HttpResponse<OrderShort[]>> {
        return new Promise<HttpResponse<OrderShort[]>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/search-orders`, { search: searchValue }).subscribe({
                next: (res: any) => {
                    resolve(OrderRetestSetupMapper.mapSearchOrders(res));
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getOrderById(orderId: string): Promise<HttpResponse<{ order: OrderShort | null }>> {
        return new Promise<HttpResponse<{ order: OrderShort | null }>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/get-order/` + orderId).subscribe({
                next: (res: any) => {
                    resolve(OrderRetestSetupMapper.mapGetOrder(res));
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    prepareStep2(orderId: number): Promise<HttpResponse<OrderSampleDetails>> {
        return new Promise<HttpResponse<OrderSampleDetails>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/prepare-step-2/` + orderId).subscribe({
                next: (res: any) => {
                    resolve(OrderRetestSetupMapper.mapPrepareStep2Retest(res));
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                    displayOrderNotFoundPage(this.router);
                },
            });
        });
    }

    prepareStep3(orderId: number): Promise<HttpResponse<PrepareStep3Interface>> {
        return new Promise<HttpResponse<PrepareStep3Interface>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/prepare-step-3/` + orderId).subscribe({
                next: (res: any) => {
                    resolve(OrderRetestSetupMapper.mapPrepareStep3Retest(res));
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                    displayOrderNotFoundPage(this.router);
                },
            });
        });
    }

    getEstimateAmount(samples: OrderDetailsReaction[]): Promise<HttpResponse<EstimatedAmount>> {
        const body = {
            samples: samples.map(sample => {
                return {
                    name: sample.sampleName!,
                    primerNames: sample.primers.map(primer => primer.name),
                };
            }),
        };

        return new Promise<HttpResponse<EstimatedAmount>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/estimate-amount`, body).subscribe({
                next: (res: any) => {
                    resolve(OrderRetestSetupMapper.mapEstimateAmount(res));
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    submitRetest(body: SubmitRetestPayload): Promise<HttpResponse<SubmitRetestResponse>> {
        return new Promise<HttpResponse<SubmitRetestResponse>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/submit-retest`, body).subscribe({
                next: res => {
                    resolve({ data: res, status: HttpStatusEnum.SUCCESS });
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }
}
